/**
 * BatchExecutionDashboard Page
 * Displays results of the most recent batch pipeline run
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Download, ChevronDown, ChevronUp } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { exportBatchResultsAsCSV, downloadBlob, generateFilename } from '@/lib/exportFormats';

type StatusFilter = 'all' | 'completed' | 'blocked' | 'error' | 'skipped';

export function BatchExecutionDashboard() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  // Query batch results
  const { data: results, isLoading } = trpc.system.getBatchExecutionResults.useQuery();

  // Filter and search
  const filteredResults = useMemo(() => {
    if (!results) return [];

    let all = [
      ...results.completed.map((r: any) => ({ ...r, type: 'completed' as const })),
      ...results.skipped_already_complete.map((r: any) => ({ ...r, type: 'skipped' as const })),
      ...results.blocked.map((r: any) => ({ ...r, type: 'blocked' as const })),
      ...results.guard_blocked.map((r: any) => ({ ...r, type: 'guard_blocked' as const })),
      ...results.errors.map((r: any) => ({ ...r, type: 'error' as const })),
    ];

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'blocked') {
        all = all.filter((r) => r.type === 'blocked' || r.type === 'guard_blocked');
      } else if (statusFilter === 'error') {
        all = all.filter((r) => r.type === 'error');
      } else if (statusFilter === 'skipped') {
        all = all.filter((r) => r.type === 'skipped');
      } else if (statusFilter === 'completed') {
        all = all.filter((r) => r.type === 'completed');
      }
    }

    // Apply search
    if (searchTerm) {
      all = all.filter((r) => r.recordId.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    return all;
  }, [results, statusFilter, searchTerm]);

  const paginatedResults = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredResults.slice(start, start + PAGE_SIZE);
  }, [filteredResults, page]);

  const toggleRow = (caseId: string) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(caseId)) {
      newSet.delete(caseId);
    } else {
      newSet.add(caseId);
    }
    setExpandedRows(newSet);
  };

  const handleExportCSV = async () => {
    if (!results) return;
    setExporting(true);
    try {
      const blob = await exportBatchResultsAsCSV(results);
      downloadBlob(blob, generateFilename('batch-results', 'csv'));
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (type: string) => {
    switch (type) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'skipped':
        return <Badge className="bg-blue-100 text-blue-800">Skipped</Badge>;
      case 'blocked':
      case 'guard_blocked':
        return <Badge className="bg-yellow-100 text-yellow-800">Blocked</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-800">Error</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!results) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Batch Execution Dashboard</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-gray-500">No batch results available</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Cases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{results.summary.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{results.summary.completed}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Already Done</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{results.summary.skipped_already_complete}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Blocked</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {results.summary.blocked_by_truth + results.summary.blocked_by_guards}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Errors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{results.summary.errors}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Export */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Execution Results</CardTitle>
              <CardDescription>{filteredResults.length} results</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              disabled={exporting}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <Input
              placeholder="Search by Record ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              className="flex-1"
            />

            <div className="flex gap-2">
              {(['all', 'completed', 'skipped', 'blocked', 'error'] as StatusFilter[]).map((filter) => (
                <Button
                  key={filter}
                  variant={statusFilter === filter ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setStatusFilter(filter);
                    setPage(0);
                  }}
                  className="capitalize"
                >
                  {filter}
                </Button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Record ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Stages</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedResults.map((result) => (
                  <div key={result.caseId}>
                    <TableRow>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleRow(result.caseId)}
                          className="h-6 w-6 p-0"
                        >
                          {expandedRows.has(result.caseId) ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{result.recordId}</TableCell>
                      <TableCell>{getStatusBadge(result.type)}</TableCell>
                      <TableCell className="text-sm">
                        {Object.keys(result.stages).length} stages
                      </TableCell>
                      <TableCell className="text-sm text-red-600 max-w-xs truncate">
                        {result.error || '—'}
                      </TableCell>
                    </TableRow>

                    {/* Expanded row */}
                    {expandedRows.has(result.caseId) && (
                      <TableRow>
                        <TableCell colSpan={5} className="bg-gray-50 p-4">
                          <div className="space-y-2">
                            <div className="text-sm font-semibold">Stages:</div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {Object.entries(result.stages).map(([stage, info]: [string, any]) => (
                                <div key={stage} className="text-xs bg-white p-2 rounded border">
                                  <div className="font-mono">{stage}</div>
                                  <div className="text-gray-600">
                                    {info?.skipped ? 'skipped' : info?.success ? 'ok' : 'failed'}
                                  </div>
                                  {info?.reason && <div className="text-gray-500 mt-1">{info.reason}</div>}
                                </div>
                              ))}
                            </div>
                            {result.error && (
                              <div className="text-xs bg-red-50 p-2 rounded border border-red-200">
                                <div className="font-semibold text-red-800">Error:</div>
                                <div className="text-red-700 mt-1">{result.error}</div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </div>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {filteredResults.length > PAGE_SIZE && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {page * PAGE_SIZE + 1} to {Math.min((page + 1) * PAGE_SIZE, filteredResults.length)} of{' '}
                {filteredResults.length}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= filteredResults.length}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-xs text-gray-500 text-center">
        Last updated: {new Date(results.timestamp).toLocaleString()}
      </div>
    </div>
  );
}
